import { X, Calendar, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { useIndustryModal } from "@/context/IndustryModalContext";
import { useBookDemo } from "@/context/BookDemoContext";
import { INDUSTRIES_BY_KEY } from "@/data/industries";

export default function IndustryModal() {
    const { active, close } = useIndustryModal();
    const { open: openDemo } = useBookDemo();
    const data = active ? INDUSTRIES_BY_KEY[active] : null;
    const Icon = data?.icon;

    return (
        <DialogPrimitive.Root open={!!active} onOpenChange={(o) => !o && close()}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    data-testid="industry-modal-overlay"
                    className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                />
                <DialogPrimitive.Content
                    data-testid="industry-modal"
                    className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100vw-2rem)] max-w-2xl bg-white rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-200/70 p-6 sm:p-8 max-h-[calc(100vh-2rem)] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                >
                    <DialogPrimitive.Close
                        data-testid="industry-modal-close"
                        className="absolute right-4 top-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        aria-label="Luk"
                    >
                        <X className="w-4 h-4" />
                    </DialogPrimitive.Close>

                    {data && (
                        <>
                            <div className="flex items-start gap-4 pr-10">
                                <span className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                    {Icon && <Icon className="w-5 h-5" />}
                                </span>
                                <div className="min-w-0">
                                    <DialogPrimitive.Title className="text-2xl font-bold text-slate-900">
                                        {data.name}
                                    </DialogPrimitive.Title>
                                    <DialogPrimitive.Description className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                                        {data.intro}
                                    </DialogPrimitive.Description>
                                </div>
                            </div>

                            <div className="mt-6 space-y-5">
                                <div>
                                    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-rose-600">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        Typiske udfordringer
                                    </h4>
                                    <ul className="mt-3 space-y-2">
                                        {data.challenges.map((c, i) => (
                                            <li key={i} data-testid={`industry-challenge-${i}`} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-rose-400" />
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Sådan hjælper MyServiceHelper
                                    </h4>
                                    <ul className="mt-3 space-y-2">
                                        {data.helps.map((c, i) => (
                                            <li key={i} data-testid={`industry-help-${i}`} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                                </span>
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-700">
                                        <FileText className="w-3.5 h-3.5" />
                                        Eksempel på opgave
                                    </h4>
                                    <p className="mt-2 text-sm text-slate-700 leading-relaxed italic">
                                        “{data.example}”
                                    </p>
                                </div>
                            </div>

                            <div className="mt-7 pt-5 border-t border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <p className="text-xs text-slate-500">Vil du se det i din branche?</p>
                                <Button
                                    data-testid="industry-modal-cta"
                                    onClick={() => { close(); setTimeout(openDemo, 150); }}
                                    className="rounded-full h-11 px-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                                >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Book gratis demo
                                </Button>
                            </div>
                        </>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
